import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useI18n } from "../lib/i18n";
import { fmtUSD } from "../lib/format";
import { ComparisonChart } from "../components/GrowthChart";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Trophy, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS_IT = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function buildInputs({ initial, monthly, withdrawal }) {
  const arr = Array.from({ length: 120 }, () => ({
    deposit: 0, withdrawal_value: 0, withdrawal_type: "usd", withdrawal_mode: "none",
  }));
  if (initial > 0) arr[0] = { ...arr[0], deposit: initial };
  if (monthly > 0) {
    for (let i = 1; i < 120; i++) {
      arr[i] = { ...arr[i], deposit: monthly };
    }
  }
  if (withdrawal > 0) {
    arr[0] = { ...arr[0], withdrawal_value: withdrawal, withdrawal_type: "usd", withdrawal_mode: "continuous" };
  }
  return arr;
}

function scenarioPayload({ startYear, startMonth, initial, monthly, withdrawal }) {
  const inputs = buildInputs({ initial: Number(initial) || 0, monthly: Number(monthly) || 0, withdrawal: Number(withdrawal) || 0 });
  return {
    start_year: Number(startYear),
    start_month: Number(startMonth),
    months: 120,
    inputs: inputs
      .map((row, idx) => (row.deposit > 0 || row.withdrawal_value > 0 ? { month_index: idx, ...row } : null))
      .filter(Boolean),
  };
}

function ScenarioForm({ label, values, onChange, color, testPrefix }) {
  const { t } = useI18n();
  return (
    <div className="wc-sc-form" style={{ borderColor: color }}>
      <div className="wc-sc-title" style={{ color }}>
        <span className="wc-sc-badge" style={{ background: color }}>{label}</span>
      </div>
      <div className="wc-sc-fields">
        <div className="wc-field">
          <label>{t("initial_deposit_lbl")}</label>
          <Input
            type="number"
            min="0"
            step="500"
            value={values.initial}
            onChange={(e) => onChange("initial", e.target.value)}
            className="wc-cell-input"
            data-testid={`${testPrefix}-initial`}
          />
        </div>
        <div className="wc-field">
          <label>{t("monthly_deposit")}</label>
          <Input
            type="number"
            min="0"
            step="100"
            value={values.monthly}
            onChange={(e) => onChange("monthly", e.target.value)}
            className="wc-cell-input"
            data-testid={`${testPrefix}-monthly`}
          />
        </div>
        <div className="wc-field">
          <label>{t("monthly_withdrawal")}</label>
          <Input
            type="number"
            min="0"
            step="50"
            value={values.withdrawal}
            onChange={(e) => onChange("withdrawal", e.target.value)}
            className="wc-cell-input"
            data-testid={`${testPrefix}-withdrawal`}
          />
        </div>
      </div>
    </div>
  );
}

export default function Compare() {
  const { t, lang } = useI18n();
  const monthNames = lang === "it" ? MONTHS_IT : MONTHS_EN;

  const [startYear, setStartYear] = useState(2026);
  const [startMonth, setStartMonth] = useState(9);
  const [scA, setScA] = useState({ initial: "1000", monthly: "0", withdrawal: "0" });
  const [scB, setScB] = useState({ initial: "1000", monthly: "500", withdrawal: "0" });
  const [simA, setSimA] = useState(null);
  const [simB, setSimB] = useState(null);

  const runBoth = useCallback(async () => {
    try {
      const [rA, rB] = await Promise.all([
        axios.post(`${API}/simulate`, scenarioPayload({ startYear, startMonth, ...scA })),
        axios.post(`${API}/simulate`, scenarioPayload({ startYear, startMonth, ...scB })),
      ]);
      setSimA(rA.data);
      setSimB(rB.data);
    } catch (e) {
      console.error(e);
    }
  }, [startYear, startMonth, scA, scB]);

  useEffect(() => {
    const id = setTimeout(runBoth, 300);
    return () => clearTimeout(id);
  }, [runBoth]);

  const diff = simA && simB ? simA.final_total - simB.final_total : 0;
  const winner = diff > 0 ? "A" : diff < 0 ? "B" : null;

  return (
    <div className="wc-container wc-compare" data-testid="compare-page">
      <div className="wc-hero">
        <div className="wc-hero-copy">
          <span className="wc-eyebrow">{t("nav_compare")}</span>
          <h1 className="wc-title">{t("compare_title")}</h1>
          <p className="wc-lead">{t("compare_subtitle")}</p>
        </div>
        <div className="wc-hero-controls">
          <div className="wc-field">
            <label>{t("year")}</label>
            <Select value={String(startYear)} onValueChange={(v) => setStartYear(Number(v))}>
              <SelectTrigger className="wc-select" data-testid="cmp-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 8 }, (_, i) => 2025 + i).map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="wc-field">
            <label>{t("month")}</label>
            <Select value={String(startMonth)} onValueChange={(v) => setStartMonth(Number(v))}>
              <SelectTrigger className="wc-select" data-testid="cmp-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthNames.map((mn, i) => (
                  <SelectItem key={i} value={String(i + 1)}>{mn}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="wc-sc-grid">
        <ScenarioForm
          label={t("scenario_a")}
          values={scA}
          onChange={(k, v) => setScA((s) => ({ ...s, [k]: v }))}
          color="#f5c76e"
          testPrefix="sc-a"
        />
        <ScenarioForm
          label={t("scenario_b")}
          values={scB}
          onChange={(k, v) => setScB((s) => ({ ...s, [k]: v }))}
          color="#7dd18c"
          testPrefix="sc-b"
        />
      </div>

      {simA && simB && (
        <>
          <div className="wc-sc-results">
            <div className="wc-sc-card wc-sc-card-a" data-testid="result-a">
              <span className="wc-sc-name">{t("scenario_a")}</span>
              <div className="wc-sc-row"><span>{t("tot_deposited")}</span><b>{fmtUSD(simA.total_deposited, lang)}</b></div>
              <div className="wc-sc-row"><span>{t("tot_withdrawn")}</span><b>{fmtUSD(simA.total_withdrawn, lang)}</b></div>
              <div className="wc-sc-row wc-sc-row-final"><span>{t("final_total")}</span><b>{fmtUSD(simA.final_total, lang)}</b></div>
              <div className="wc-sc-row"><span>{t("growth")}</span><b style={{color: '#7dd18c'}}>{fmtUSD(simA.total_growth, lang)}</b></div>
            </div>

            <div className="wc-sc-diff">
              <Trophy size={28} />
              <span className="wc-sc-diff-label">{t("winner")}</span>
              <span className="wc-sc-diff-winner" data-testid="cmp-winner">
                {winner ? `Scenario ${winner}` : "—"}
              </span>
              <span className="wc-sc-diff-label">{t("diff_label")}</span>
              <span className="wc-sc-diff-value" data-testid="cmp-diff">{fmtUSD(Math.abs(diff), lang)}</span>
            </div>

            <div className="wc-sc-card wc-sc-card-b" data-testid="result-b">
              <span className="wc-sc-name">{t("scenario_b")}</span>
              <div className="wc-sc-row"><span>{t("tot_deposited")}</span><b>{fmtUSD(simB.total_deposited, lang)}</b></div>
              <div className="wc-sc-row"><span>{t("tot_withdrawn")}</span><b>{fmtUSD(simB.total_withdrawn, lang)}</b></div>
              <div className="wc-sc-row wc-sc-row-final"><span>{t("final_total")}</span><b>{fmtUSD(simB.final_total, lang)}</b></div>
              <div className="wc-sc-row"><span>{t("growth")}</span><b style={{color: '#7dd18c'}}>{fmtUSD(simB.total_growth, lang)}</b></div>
            </div>
          </div>

          <section className="wc-chart-section">
            <div className="wc-chart-header">
              <div>
                <h2 className="wc-h2">{t("chart_title")}</h2>
                <p className="wc-lead" style={{fontSize:14, marginTop:-4}}>
                  {lang === "it" ? "Totale (capitale + liquidità) di ogni scenario" : "Total (capital + cash) for each scenario"}
                </p>
              </div>
            </div>
            <ComparisonChart rowsA={simA.rows} rowsB={simB.rows} lang={lang} />
          </section>
        </>
      )}
    </div>
  );
}
